import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { feedbackTypesQueryOptions } from '@/features/feedbackTypes/api/feedbackTypes.api';
import {
  zonalManagerReportingToQueryOptions,
  zonalManagersQueryOptions,
} from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { feedbackListInfiniteQueryOptions } from '../api/feedback.api';
import { useFeedbackListFilters } from '../hooks/useFeedbackListFilters';
import { getFeedbackListFilterFields } from '../utils/getFeedbackListFilterFields';
import { getFeedbackColumns } from './FeedbackColumns';

export function FeedbackListTable() {
  const controller = useFeedbackListFilters();
  const { params } = controller;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const lastViewedFeedbackId = useRevealLastViewedRow('feedbacks');

  const { data: feedbackTypes } = useQuery(feedbackTypesQueryOptions());
  const { data: reportingMembers } = useQuery(zonalManagerReportingToQueryOptions());
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(feedbackListInfiniteQueryOptions(params));

  const columns = useMemo(() => getFeedbackColumns(params.search), [params.search]);

  const filterFields = useMemo(
    () => getFeedbackListFilterFields({ feedbackTypes, reportingMembers, zonalManagers }),
    [feedbackTypes, reportingMembers, zonalManagers],
  );

  const totals = firstPageMeta?.totals;
  const stats: LabeledStat[] = [
    { key: 'total', label: 'Total', value: firstPageMeta?.total },
    { key: 'notComplete', label: 'Not Complete', value: totals?.notComplete },
    { key: 'complete', label: 'Complete', value: totals?.complete },
    { key: 'unread', label: 'Unread', value: totals?.unread },
  ];

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Feedbacks"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: 'Search feedback',
        }}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={params.startDate}
            dateTo={params.endDate}
          />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel fields={filterFields} {...getFilterPanelProps(controller)} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load feedback"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid stats={stats} isLoading={isLoading} />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="user"
            className="lg:flex-1"
            emptyMessage="No feedback found"
            emptyHint="Try another filter, or set Complete Status to All."
            isRowHighlighted={(feedback) => feedback.id === lastViewedFeedbackId}
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
